import { memo, useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { ActionIcon, Box, Group, Text, Tooltip } from '@mantine/core';
import { IconPlus, IconRowInsertBottom, IconTrash } from '@tabler/icons-react';
import {
  argumentGroups, cellId, columnLayout, CX_ROW_ID, columnStates, segmentSide, segmentSpeakers, slotLabel, type ColumnState, type FlowCell, type FlowRow, type LayoutMode, type Mark, type Round, type Segment, type Sheet,
} from '@flow/core';
import { useLiveQuery } from 'dexie-react-hooks';
import { useDeps, useUseCases } from '../../app/services';
import { ArgumentTools } from './ArgumentTools';
import { AiTitleStatus } from '../ai/AiTitleStatus';
import { useAutoTitles } from '../ai/useAutoTitles';
import './flow.css';

const MARKS: { m: Mark; key: string; label: string; short: string }[] = [
  { m: 'extended', key: 'e', label: 'Extended', short: '✓ ext' },
  { m: 'dropped', key: 'd', label: 'Dropped', short: '✗ drop' },
  { m: 'turn', key: 't', label: 'Turn', short: 'T' },
  { m: 'conceded', key: 'c', label: 'Conceded', short: 'conc' },
  { m: 'star', key: 's', label: 'Important', short: '★' },
  { m: 'unclear', key: 'u', label: 'Unclear', short: '?' },
];
const markByKey = new Map(MARKS.map((x) => [x.key, x.m]));
const markShort = new Map(MARKS.map((x) => [x.m, x.short]));

/** Label chips on a box; each has its own × so removing one never needs the toolbar. */
const MarkChips = ({ marks, onRemove }: { marks: Mark[]; onRemove: (m: Mark) => void }) =>
  marks.length === 0 ? null : (
    <div className="flow-marks">
      {marks.map((m) => (
        <span key={m} className="flow-mark" data-m={m}>
          {markShort.get(m)}
          <button
            type="button"
            className="flow-mark-x"
            aria-label={`Remove ${m} label`}
            onMouseDown={(e) => {
              // Don't move the cursor or steal focus from the box being typed in.
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.stopPropagation();
              onRemove(m);
            }}
          >
            ×
          </button>
        </span>
      ))}
    </div>
  );

interface Cursor {
  rowId: string;
  segId: string;
  editing: boolean;
}

/** One box. Memoized; the grid holds ~100–400 of these. */
const CellView = memo(function CellView({ cell, seg, side, state, isCursor, editing, onPointer, onRemoveMark, title, groupStart, rowId, gridRow, gridColumn, merged, flash }: {
  cell: FlowCell | undefined; seg: Segment; side: 0 | 1 | null; state: ColumnState; isCursor: boolean; editing: boolean;
  onPointer: () => void; onRemoveMark: (m: Mark) => void; title?: string | undefined; groupStart: boolean; rowId: string;
  gridRow: string; gridColumn: number; merged: boolean; flash: boolean;
}) {
  const text = cell?.text ?? '';
  return (
    <div
      className="flow-cell"
      style={{ gridRow, gridColumn }}
      data-merged={merged ? '1' : '0'}
      data-cell={`${rowId}~${seg.id}`}
      data-flash={flash ? '1' : '0'}
      data-row={rowId}
      data-group-start={groupStart ? '1' : '0'}
      data-side={side ?? 'n'}
      data-q={seg.kind === 'questioning' ? '1' : '0'}
      data-state={state}
      data-cursor={isCursor ? '1' : '0'}
      data-editing={editing ? '1' : '0'}
      data-dropped={cell?.marks.includes('dropped') ? '1' : '0'}
      onMouseDown={(e) => {
        if (!editing) {
          e.preventDefault();
          onPointer();
        }
      }}
    >
      {!editing && state === 'future' && (text.trim() || (cell?.marks.length ?? 0) > 0) && (
        <span className="flow-dot" title={text} aria-label="Has notes (open this column to read)" />
      )}
      {!editing && state !== 'future' && (
        <>
          {title && <div className="flow-title" data-testid="argument-title">{title}</div>}
          <div className="flow-text">{text}</div>
          {cell && <MarkChips marks={cell.marks} onRemove={onRemoveMark} />}
        </>
      )}
    </div>
  );
});

/** Textarea that owns its draft while you type; saves on pause, on move, and on unmount. */
const CellEditor = ({ initial, onSave, onKeyDown }: { initial: string; onSave: (text: string) => void; onKeyDown: (e: KeyboardEvent<HTMLTextAreaElement>, value: string) => void }) => {
  const [value, setValue] = useState(initial);
  const latest = useRef(initial);
  const saved = useRef(initial);
  const ref = useRef<HTMLTextAreaElement>(null);
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;
  const flush = useCallback(() => {
    if (latest.current !== saved.current) {
      saved.current = latest.current;
      onSaveRef.current(latest.current);
    }
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (el) {
      el.focus({ preventScroll: false });
      el.setSelectionRange(el.value.length, el.value.length);
    }
    return () => flush();
  }, [flush]);

  useEffect(() => {
    const id = setTimeout(flush, 400);
    return () => clearTimeout(id);
  }, [value, flush]);

  return (
    <textarea
      ref={ref}
      value={value}
      aria-label="Flow note"
      spellCheck={false}
      autoCapitalize="off"
      autoCorrect="off"
      onChange={(e) => {
        latest.current = e.currentTarget.value;
        setValue(e.currentTarget.value);
      }}
      onKeyDown={(e) => {
        if (['Enter', 'Tab', 'Escape', 'ArrowUp', 'ArrowDown'].includes(e.key)) flush();
        onKeyDown(e, value);
      }}
      onBlur={flush}
    />
  );
};

export interface FlowGridProps {
  round: Round;
  sheet: Sheet;
  rows: FlowRow[];
  cells: Map<string, FlowCell>;
  timerSegmentId: string | null;
  mode: LayoutMode;
  onSheetStep: (dir: 1 | -1) => void;
  /** Show this cell (from the argument map): cursor on it, scrolled into view, briefly highlighted. */
  jumpTo?: { rowId: string | null; segId: string; nonce: number } | null;
}

/**
 * The flow: rows are argument threads, columns are speeches (crossfire included), read left to right like paper.
 * Click or type to write; Esc for "pen up" mode where single keys mark and move.
 */
export const FlowGrid = ({ round, sheet, rows: savedRows, cells, timerSegmentId, mode, onSheetStep, jumpTo }: FlowGridProps) => {
  const uc = useUseCases();
  // New rows show up (with the cursor in them) right away; the saved copy replaces them a moment later. Waiting for
  // the save first let keystrokes typed in between land in the previous row.
  const [pending, setPending] = useState<{ row: FlowRow; after?: string | undefined; before?: string | undefined; first?: boolean | undefined }[]>([]);
  const rows = useMemo(() => {
    const saved = new Set(savedRows.map((r) => r.id));
    const out = [...savedRows];
    for (const p of pending) {
      if (saved.has(p.row.id)) continue;
      const anchor = out.findIndex((r) => r.id === (p.after ?? p.before));
      if (p.first) out.unshift(p.row);
      else if (anchor < 0) out.push(p.row);
      else out.splice(p.after ? anchor + 1 : anchor, 0, p.row);
    }
    return out;
  }, [savedRows, pending]);
  useEffect(() => {
    const saved = new Set(savedRows.map((r) => r.id));
    setPending((p) => (p.some((x) => saved.has(x.row.id)) ? p.filter((x) => !saved.has(x.row.id)) : p));
  }, [savedRows]);
  const createRow = useCallback((sheetId: string, pos: { after?: string; before?: string; first?: boolean } = {}) => {
    const id = crypto.randomUUID ? crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    setPending((p) => [...p, { row: { id, roundId: round.id, sheetId, rank: 0, createdAt: Date.now() }, ...pos }]);
    void uc.addRow(round.id, sheetId, { ...pos, id }).then((r) => {
      if (!r.ok) setPending((p) => p.filter((x) => x.row.id !== id)); // e.g. the anchor row was deleted meanwhile
    });
    return id;
  }, [uc, round.id]);
  const deps = useDeps();
  // Device-local debater names, for the column headers ("C1 Lincoln AB · Ana").
  const names = useLiveQuery(() => deps.names.get(round.id), [round.id]);
  const gridRef = useRef<HTMLDivElement>(null);
  const [cursor, setCursor] = useState<Cursor | null>(null);
  const editingCellId = cursor?.editing ? cellId(cursor.rowId, cursor.segId) : null;
  // Arguments: a row with text in the first column starts one; the rows under it are its responses.
  const groups = useMemo(() => argumentGroups(sheet, round.format, rows, cells), [sheet, round.format, rows, cells]);
  const heads = useMemo(() => new Set(groups.slice(1).map((g) => g.headRowId)), [groups]);
  const titles = useMemo(() => new Map(rows.flatMap((r) => (r.title ? [[r.id, r.title] as const] : []))), [rows]);
  useAutoTitles(round, groups, titles, editingCellId);
  const segments = useMemo(() => sheet.segmentIds.map((id) => round.format.segments.find((s) => s.id === id)!).filter(Boolean), [round.format, sheet]);
  const rowIds = rows.map((r) => r.id);
  // Cross-ex/crossfire columns are one big box spanning every row, unless this round already has per-row notes there.
  const merged = useMemo(() => {
    const ids = new Set(rowIds);
    const legacy = new Set([...cells.values()].filter((c) => ids.has(c.rowId) && (c.text.trim() || c.marks.length)).map((c) => c.segmentId));
    return new Set(segments.filter((s) => s.kind === 'questioning' && !legacy.has(s.id)).map((s) => s.id));
  }, [segments, cells, rowIds.join('|')]);
  /** Which stored row a (row, column) position writes to: the shared cross-ex box for merged columns. */
  const cellRow = useCallback((rowId: string, segId: string) => (merged.has(segId) ? CX_ROW_ID : rowId), [merged]);

  // The column you're writing in is always wide; otherwise follow the timer.
  // A jump opens its column until the timer moves on, even if that speech hasn't happened yet.
  const [peekSeg, setPeekSeg] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  useEffect(() => setPeekSeg(null), [timerSegmentId]);
  const layoutSeg = cursor?.editing ? cursor.segId : peekSeg ?? timerSegmentId;
  const states = useMemo(() => columnStates(round.format, sheet.segmentIds, layoutSeg), [round.format, sheet.segmentIds, layoutSeg]);
  const activeCol = Math.max(0, states.indexOf('active'));
  const withContent = useMemo(() => {
    const ids = new Set(rowIds);
    const out = new Set<string>();
    for (const c of cells.values()) if ((ids.has(c.rowId) || c.rowId === CX_ROW_ID) && c.text.trim()) out.add(c.segmentId);
    return out;
  }, [cells, rowIds.join('|')]);
  const { template, collapsed } = columnLayout(segments, states, mode, activeCol, withContent);
  // A collapsed column renders like a future one: header label only.
  const shownState = (i: number): ColumnState => (collapsed[i] ? 'future' : states[i]!);

  useEffect(() => {
    if (!jumpTo || !sheet.segmentIds.includes(jumpTo.segId)) return;
    const rowId = jumpTo.rowId && rowIds.includes(jumpTo.rowId) ? jumpTo.rowId : cursor?.rowId ?? rowIds[0];
    if (!rowId) return;
    setCursor({ rowId, segId: jumpTo.segId, editing: false });
    setPeekSeg(jumpTo.segId);
    const target = `${merged.has(jumpTo.segId) ? CX_ROW_ID : rowId}~${jumpTo.segId}`;
    setFlash(target);
    const t = setTimeout(() => setFlash(null), 1600);
    requestAnimationFrame(() => {
      gridRef.current?.focus();
      gridRef.current?.querySelector(`[data-cell="${target}"]`)?.scrollIntoView({ block: 'center', inline: 'center', behavior: 'smooth' });
    });
    return () => clearTimeout(t);
  }, [jumpTo?.nonce]);

  // Keep the column being written in on screen when the flow is wider than the window.
  useEffect(() => {
    if (!layoutSeg) return;
    gridRef.current?.querySelector(`[data-testid="col-${layoutSeg}"]`)?.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: 'smooth' });
  }, [layoutSeg]);

  // Focus follows the timer: when the speech changes, move the cursor to that column on the same row.
  const prevTimerSeg = useRef(timerSegmentId);
  useEffect(() => {
    if (prevTimerSeg.current === timerSegmentId) return;
    prevTimerSeg.current = timerSegmentId;
    if (timerSegmentId && sheet.segmentIds.includes(timerSegmentId)) {
      setCursor((c) => (c ? { ...c, segId: timerSegmentId } : c));
    }
  }, [timerSegmentId, sheet.segmentIds]);

  // Keep the cursor valid if its row is deleted. Only rows we've seen count: a row created a moment ago (Enter on the
  // last row, or the first click before the starter rows exist) isn't in the list yet, and dropping the cursor then
  // would throw away what's being typed.
  const seenRows = useRef(new Set<string>());
  useEffect(() => {
    for (const id of rowIds) seenRows.current.add(id);
    if (cursor && seenRows.current.has(cursor.rowId) && !rowIds.includes(cursor.rowId)) setCursor(null);
  }, [rowIds, cursor]);

  const save = useCallback((rowId: string, segId: string, text: string) => void uc.writeCell(round.id, cellRow(rowId, segId), segId, text), [uc, round.id, cellRow]);

  const move = useCallback(async (dRow: number, dCol: number, opts: { editing?: boolean; createAtEnd?: boolean } = {}) => {
    if (!cursor) return;
    const r = rowIds.indexOf(cursor.rowId);
    const c = sheet.segmentIds.indexOf(cursor.segId);
    let nr = r + dRow;
    const nc = Math.max(0, Math.min(sheet.segmentIds.length - 1, c + dCol));
    if (nr >= rowIds.length && opts.createAtEnd) {
      setCursor({ rowId: createRow(sheet.id), segId: sheet.segmentIds[nc]!, editing: opts.editing ?? cursor.editing });
      return;
    }
    nr = Math.max(0, Math.min(rowIds.length - 1, nr));
    setCursor({ rowId: rowIds[nr]!, segId: sheet.segmentIds[nc]!, editing: opts.editing ?? cursor.editing });
  }, [cursor, rowIds, sheet, createRow]);

  const insertRow = useCallback(async (where: 'after' | 'before') => {
    const anchor = cursor?.rowId;
    // New rows join the anchor's own sheet, so they land next to it even when one sheet shows rows from several.
    const anchorSheet = rows.find((r) => r.id === anchor)?.sheetId ?? sheet.id;
    const id = createRow(anchorSheet, anchor ? { [where]: anchor } : {});
    setCursor({ rowId: id, segId: cursor?.segId ?? sheet.segmentIds[0]!, editing: true });
  }, [cursor, rows, createRow, sheet]);

  /** Start writing in a column. If the sheet has no rows yet (just opened), create one first. */
  const startAt = useCallback(async (segId: string) => {
    const rowId = cursor?.rowId && rowIds.includes(cursor.rowId) ? cursor.rowId : rowIds[0];
    if (rowId) return setCursor({ rowId, segId, editing: true });
    // Rows haven't loaded yet (brand-new round): this row goes first, above the starter rows about to arrive.
    setCursor({ rowId: createRow(sheet.id, { first: true }), segId, editing: true });
  }, [cursor, rowIds, createRow, sheet.id]);

  const toggleMark = useCallback((m: Mark) => {
    if (cursor) void uc.toggleMark(round.id, cellRow(cursor.rowId, cursor.segId), cursor.segId, m);
  }, [cursor, uc, round.id, cellRow]);
  const removeMark = useCallback((rowId: string, segId: string, m: Mark) => void uc.toggleMark(round.id, rowId, segId, m), [uc, round.id]);
  const cursorMarks = cursor ? cells.get(cellId(cellRow(cursor.rowId, cursor.segId), cursor.segId))?.marks ?? [] : [];

  const deleteRow = useCallback(async () => {
    if (!cursor) return;
    const i = rowIds.indexOf(cursor.rowId);
    const hasText = sheet.segmentIds.some((s) => cells.get(cellId(cursor.rowId, s))?.text.trim());
    if (hasText && !confirm('Delete this whole row (argument) and its notes?')) return;
    await uc.deleteRow(cursor.rowId);
    const next = rowIds[i + 1] ?? rowIds[i - 1];
    setCursor(next ? { ...cursor, rowId: next, editing: false } : null);
  }, [cursor, rowIds, cells, sheet.segmentIds, uc]);

  const editorKeys = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    const ta = e.currentTarget;
    const mod = e.metaKey || e.ctrlKey;
    if (e.altKey) return; // global shortcuts
    // The cross-ex box is a notepad: Enter is a new line and arrows move within the text. Tab / Esc still leave it.
    const inMerged = !!cursor && merged.has(cursor.segId);
    if (inMerged && (e.key === 'Enter' || e.key === 'ArrowUp' || e.key === 'ArrowDown')) return;
    if (e.key === 'Enter' && mod) {
      e.preventDefault();
      void insertRow('after');
    } else if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void move(1, 0, { createAtEnd: true, editing: true });
    } else if (e.key === 'Tab') {
      e.preventDefault();
      void move(0, e.shiftKey ? -1 : 1, { editing: true });
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setCursor((c) => (c ? { ...c, editing: false } : c));
      gridRef.current?.focus();
    } else if (e.key === 'ArrowUp' && !ta.value.slice(0, ta.selectionStart).includes('\n')) {
      e.preventDefault();
      void move(-1, 0);
    } else if (e.key === 'ArrowDown' && !ta.value.slice(ta.selectionEnd).includes('\n')) {
      e.preventDefault();
      void move(1, 0);
    }
  };

  const navKeys = (e: KeyboardEvent<HTMLDivElement>) => {
    // Deleting a row is deliberately button-only (toolbar trash), so it can't happen by accident.
    if (e.target !== gridRef.current || e.altKey || e.metaKey || e.ctrlKey) return;
    if (!cursor || !rowIds.includes(cursor.rowId)) {
      e.preventDefault();
      if (rowIds[0]) setCursor({ rowId: rowIds[0], segId: timerSegmentId && sheet.segmentIds.includes(timerSegmentId) ? timerSegmentId : sheet.segmentIds[0]!, editing: false });
      return;
    }
    const k = e.key;
    const handled = () => e.preventDefault();
    if (k === 'ArrowDown' || k === 'j') { handled(); void move(1, 0); }
    else if (k === 'ArrowUp' || k === 'k') { handled(); void move(-1, 0); }
    else if (k === 'ArrowRight' || k === 'l') { handled(); void move(0, 1); }
    else if (k === 'ArrowLeft' || k === 'h') { handled(); void move(0, -1); }
    else if (k === 'Enter' || k === 'i') { handled(); setCursor({ ...cursor, editing: true }); }
    else if (k === 'o') { handled(); void insertRow('after'); }
    else if (k === 'O') { handled(); void insertRow('before'); }
    else if (k === '[') { handled(); onSheetStep(-1); }
    else if (k === ']') { handled(); onSheetStep(1); }
    else if (k === 'x' || k === 'Delete' || k === 'Backspace') { handled(); void uc.writeCell(round.id, cellRow(cursor.rowId, cursor.segId), cursor.segId, ''); }
    else if (markByKey.has(k)) { handled(); toggleMark(markByKey.get(k)!); }
  };

  return (
    <Box style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
      <Group gap={4} px="xs" py={4} wrap="nowrap" style={{ borderBottom: '1px solid var(--grid-line)', overflowX: 'auto' }}>
        {MARKS.map((x) => (
          <Tooltip key={x.m} label={`${x.label} (pen-up: ${x.key})${cursorMarks.includes(x.m) ? ' · on: tap to remove' : ''}`}>
            <ActionIcon
              variant={cursorMarks.includes(x.m) ? 'light' : 'default'}
              aria-pressed={cursorMarks.includes(x.m)}
              size="md"
              w="auto"
              px={6}
              disabled={!cursor}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => toggleMark(x.m)}
              aria-label={`Mark ${x.label}`}
              style={{ fontSize: 11, fontWeight: 700 }}
            >
              <span className="flow-mark" data-m={x.m} style={{ border: 'none', padding: 0 }}>{x.short}</span>
            </ActionIcon>
          </Tooltip>
        ))}
        <Box style={{ flex: 1 }} />
        <AiTitleStatus />
        <ArgumentTools
          round={round}
          groups={groups}
          rows={rows}
          cursorRowId={cursor?.rowId ?? null}
          onDone={() => gridRef.current?.focus()}
          onJump={(rowId) => {
            setCursor({ rowId, segId: groups.find((g) => g.headRowId === rowId)?.caseSegId ?? sheet.segmentIds[0]!, editing: false });
            gridRef.current?.focus();
            gridRef.current?.querySelector(`[data-row="${rowId}"]`)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
          }}
        />
        <Tooltip label="New row below (⌘Enter, pen-up: o)">
          <ActionIcon onMouseDown={(e) => e.preventDefault()} onClick={() => void insertRow('after')} aria-label="Insert row below"><IconRowInsertBottom size={16} /></ActionIcon>
        </Tooltip>
        <Tooltip label="Delete this row (asks first if it has notes)">
          <ActionIcon onMouseDown={(e) => e.preventDefault()} onClick={() => void deleteRow()} disabled={!cursor} aria-label="Delete row"><IconTrash size={16} /></ActionIcon>
        </Tooltip>
      </Group>
      <Box style={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
        <div
          ref={gridRef}
          className="flow-grid"
          style={{ gridTemplateColumns: template }}
          tabIndex={0}
          onKeyDown={navKeys}
          role="grid"
          aria-label={`${sheet.title} flow`}
          data-testid="flow-grid"
        >
          {segments.map((seg, i) => {
            const side = segmentSide(round, seg);
            const title = seg.by === 'both' ? seg.label : `${slotLabel(round, seg.by)} ${seg.label}`;
            const who = segmentSpeakers(round, seg, names);
            const detail = who ? [who.team, ...who.names].join(' · ') : seg.by === 'both' ? '' : slotLabel(round, seg.by);
            return (
              <Tooltip key={seg.id} label={`${title} (${Math.floor(seg.durationSec / 60)}:${String(seg.durationSec % 60).padStart(2, '0')})${who ? ` · ${detail}` : ''}`}>
                <div
                  className="flow-head"
                  data-state={shownState(i)}
                  data-side={side ?? 'n'}
                  style={{ gridRow: 1, gridColumn: i + 1, color: side === null ? 'var(--side-none)' : `var(--side-${side})` }}
                  onClick={() => void startAt(seg.id)}
                  data-testid={`col-${seg.id}`}
                >
                  {seg.id}{shownState(i) === 'future' && withContent.has(seg.id) && <span className="flow-dot flow-dot-head" aria-label="has notes" />}{shownState(i) !== 'future' && detail && <Text span size="xs" c="dimmed" ml={4} data-testid={`col-${seg.id}-who`}>{detail}</Text>}
                </div>
              </Tooltip>
            );
          })}
          {/* Cells are placed by explicit grid row/column, so the DOM order can stay stable (by row id). If rows reorder
              (e.g. starter rows arriving after the first click), the box being typed in keeps its identity and focus. */}
          {rows.map((row, ri) => ({ row, ri })).sort((a, b) => (a.row.id < b.row.id ? -1 : 1)).flatMap(({ row, ri }) =>
            segments.map((seg, i) => {
              const isMerged = merged.has(seg.id);
              if (isMerged && ri > 0) return null; // one box spans every row
              const storeRow = isMerged ? CX_ROW_ID : row.id;
              const id = cellId(storeRow, seg.id);
              const gridRow = isMerged ? `2 / span ${rows.length}` : String(ri + 2);
              const gridColumn = i + 1;
              const isCursor = cursor?.segId === seg.id && (isMerged || cursor.rowId === row.id);
              const editing = isCursor && cursor.editing;
              const cell = cells.get(id);
              const title = i === 0 ? titles.get(row.id) : undefined;
              const groupStart = !isMerged && heads.has(row.id);
              if (editing) {
                return (
                  <div key={id} className="flow-cell" style={{ gridRow, gridColumn }} data-merged={isMerged ? '1' : '0'} data-row={storeRow} data-group-start={groupStart ? '1' : '0'} data-side={segmentSide(round, seg) ?? 'n'} data-q={seg.kind === 'questioning' ? '1' : '0'} data-state="active" data-cursor="1" data-editing="1">
                    {title && <div className="flow-title">{title}</div>}
                    <CellEditor key={id} initial={cell?.text ?? ''} onSave={(t) => save(row.id, seg.id, t)} onKeyDown={(e) => editorKeys(e)} />
                    {cell && <MarkChips marks={cell.marks} onRemove={(m) => removeMark(storeRow, seg.id, m)} />}
                  </div>
                );
              }
              return (
                <CellView
                  key={id}
                  cell={cell}
                  seg={seg}
                  side={segmentSide(round, seg)}
                  state={shownState(i)}
                  isCursor={isCursor}
                  editing={false}
                  onPointer={() => setCursor({ rowId: isMerged ? (cursor?.rowId && rowIds.includes(cursor.rowId) ? cursor.rowId : row.id) : row.id, segId: seg.id, editing: true })}
                  onRemoveMark={(m) => removeMark(storeRow, seg.id, m)}
                  title={title}
                  groupStart={groupStart}
                  rowId={storeRow}
                  gridRow={gridRow}
                  gridColumn={gridColumn}
                  merged={isMerged}
                  flash={flash === id}
                />
              );
            }),
          )}
        </div>
        <Group px="xs" py={6}>
          <ActionIcon variant="subtle" onClick={() => setCursor({ rowId: createRow(sheet.id), segId: cursor?.segId ?? layoutSeg ?? sheet.segmentIds[0]!, editing: true })} aria-label="Add row at end">
            <IconPlus size={16} />
          </ActionIcon>
          <Text size="xs" c="dimmed">Enter: next row · Tab: next speech · Shift+Enter: new line · Esc: pen-up (e ✓ d ✗ t T c conc, arrows move)</Text>
        </Group>
      </Box>
    </Box>
  );
};
